import React from 'react';
import { Link } from 'react-router-dom';
import { Tooltip, Icon, Spin, Typography } from 'antd';
import cn from 'classnames';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { questionsSaveDataSelector } from '../../store/selectors';
import RequestToCloseQuestion from '../RequestToCloseQuestion/RequestToCloseQuestion';
import QuestionMeta from '../QuestionMeta/QuestionMeta';
import QuestionDescription from '../QuestionDescription/QuestionDescription';
import useQuestionAttachmentList from '../../queries/useQuestionAttachmentList';
import ImagesGallery from '../ImagesGallery/ImagesGallery';
import DownloadAttach from '../DownloadAttach/DownloadAttach';
import QuestionFooter from '../QuestionFooter/QuestionFooter';
import useQuestionMessages from '../../queries/useQuestionMessages';
import Message from '../Message/Message';
import useSortAttachmentsByExtensions from '../../../../hooks/useSortAttachmentsByExtension';
import useQuestionImgPreview from '../../queries/useQuestionImgPreview';
import useQuestionAttachment from '../../queries/useQuestionAttachment';
import { isCloseQuestionRejected } from '../../utils/isCloseQuestionRejected';
import { setPageState } from '../../../../crossPageInteractions/store/actions';
import ForceCloseQuestion from '../ForceCloseQuestion/ForceCloseQuestion';
import {
  questionOpenStateId,
  questionsRequestToCloseStateId,
} from '../../constants/common';
import { equalObjProps } from '../../store/utils';
import styles from './Question.module.scss';

const { Title, Paragraph } = Typography;

const CanNotCloseTooltipTitle = ({ canCloseDateTime }) => {
  return (
    <>
      <Title
        level={4}
        className={cn(styles.canNotCloseQuestionText, styles.title)}
      >
        Закрыть принудительно
      </Title>
      <Paragraph
        className={cn(styles.canNotCloseQuestionText)}
      >{`Вы сможете закрыть вопрос без подтверждения собственника ${
        canCloseDateTime
          ? moment(canCloseDateTime).format('DD.MM.YYYY')
          : 'через 10 дней после отправки запроса на закрытие'
      }`}</Paragraph>
      <Paragraph className={cn(styles.canNotCloseQuestionText)}>
        Чтобы закрыть раньше, обратитесь к сотруднику с соответствующими правами
      </Paragraph>
    </>
  );
};

const Question = ({ className }) => {
  const dispatch = useDispatch();

  const questionsSaveData = useSelector(
    questionsSaveDataSelector,
    equalObjProps
  );
  const selectedQuestion = questionsSaveData.selectedQuestion || {};
  const {
    id,
    stateId,
    description,
    comment,
    companyId,
    houseId,
    addressId,
    hasAttachment,
    isImportant,
    setImportantWorker,
    closeRequestDateTime,
    canClose,
    canCloseDateTime,
  } = selectedQuestion;

  const { AllowHousingStock, WorkerId } = useSelector(
    (state) => state.authentication.userInfo
  );

  const { data: attachmentList } = useQuestionAttachmentList(id, {
    enabled: hasAttachment,
  });
  const { data: messages, isFetching: isMessagesFetching } =
    useQuestionMessages(id);

  const { images, files } = useSortAttachmentsByExtensions({
    attachments: attachmentList,
  });

  const hasImportantToggle =
    !isImportant ||
    (isImportant && WorkerId && Number(WorkerId) === setImportantWorker?.id);

  return (
    <div className={cn(styles.root, className)}>
      <header className={styles.header}>
        <p className={styles.questionNum}>{id}</p>
        <div className={styles.actions}>
          {stateId === questionOpenStateId && <RequestToCloseQuestion />}
          {stateId === questionsRequestToCloseStateId && (
            <>
              <Tooltip
                title={
                  closeRequestDateTime
                    ? `Закрытие запрошено ${moment(closeRequestDateTime).format(
                        'DD.MM.YYYY'
                      )}`
                    : 'Запрошено закрытие'
                }
                placement="right"
              >
                <Icon
                  type="pause-circle"
                  theme="filled"
                  style={{ fontSize: 30, color: '#e88100' }}
                />
              </Tooltip>
              <ForceCloseQuestion
                disabled={!canClose}
                tooltipTitle={
                  !canClose ? (
                    <CanNotCloseTooltipTitle
                      canCloseDateTime={canCloseDateTime}
                    />
                  ) : (
                    'Закрыть вопрос без подтверждения собственника'
                  )
                }
              />
            </>
          )}
        </div>
      </header>
      <div className={styles.body}>
        <QuestionMeta
          question={selectedQuestion}
          hasImportantToggle={hasImportantToggle}
          hideInactiveImportantIndicator={false}
          showActions={selectedQuestion?.stateId === questionOpenStateId}
          renderTitle={(fullAddress) =>
            AllowHousingStock ? (
              <Tooltip title="Перейти на страницу с информацией по квартире">
                <Link
                  to={{
                    pathname: `/HousingStock/company/${companyId}/houses/${houseId}/flats/${addressId}`,
                    state: { from: 'Questions' },
                  }}
                  onClick={() =>
                    dispatch(
                      setPageState({
                        page: 'Questions',
                        state: questionsSaveData,
                      })
                    )
                  }
                >
                  {fullAddress}
                </Link>
              </Tooltip>
            ) : (
              fullAddress
            )
          }
        />

        {description && (
          <QuestionDescription description={description} fullDescription />
        )}

        {isCloseQuestionRejected(selectedQuestion) && (
          <div className={styles.column}>
            <p
              className={cn(
                styles.closeRejectedText,
                styles.closeRejectedTitle
              )}
            >
              Запрос на закрытие отклонен:
            </p>
            <p className={styles.closeRejectedText}>{comment}</p>
          </div>
        )}

        {images.length > 0 && (
          <ImagesGallery
            images={images}
            queryUrl="/Question/attachment"
            queryParams={{ questionId: id }}
            useImgQuery={useQuestionImgPreview}
          />
        )}

        {files.length > 0 && (
          <div className={cn(styles.column, styles.filesWrap)}>
            {files.map((file) => (
              <DownloadAttach
                attachData={file}
                key={file.id}
                useAttachQuery={useQuestionAttachment}
              />
            ))}
          </div>
        )}
        <QuestionFooter />

        <Spin spinning={isMessagesFetching}>
          {Boolean(messages?.length) && (
            <div className={styles.msgsWrap}>
              {messages.map((message, index) => (
                <Message
                  key={message.id}
                  message={message}
                  isLast={index === messages.length - 1}
                />
              ))}
            </div>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default Question;
